import { AutoComplete } from '@mediahuis/chameleon-react';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from '../../hooks/useDebounce';

export type DebouncedAutoCompleteProps = {
  label: string;
  value: string;
  dataCy: string;
  validationMessage?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  fetchSuggestions: (searchTerm: string) => Promise<string[]>;
} & typeof AutoComplete.defaultProps;

const DebouncedAutoComplete: FC<DebouncedAutoCompleteProps> = ({
  label,
  value,
  dataCy,
  onChange,
  onBlur,
  fetchSuggestions,
  validationMessage,
  ...props
}) => {
  const [searchTerm, setSearchTerm] = useState<string>(value);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const onSearch = useCallback(
    async (search: string) => {
      if (!isFocused) return;
      try {
        setLoading(true);
        const newSuggestions = await fetchSuggestions(search);
        setSuggestions(newSuggestions);
      } catch (error: unknown) {
        console.error(`Unable to fetch suggestions: ${error}`);
      } finally {
        setLoading(false);
      }
    },
    [fetchSuggestions, isFocused]
  );

  useEffect(() => {
    if (debouncedSearchTerm) {
      onSearch(debouncedSearchTerm);
    } else {
      setSuggestions([]);
    }
  }, [debouncedSearchTerm, onSearch]);

  const error = useMemo(() => validationMessage !== undefined, [validationMessage]);

  // this will make sure that on tab or on click only 1 value is in the suggestion list to make sure the normal behaviour is applied (as in normal non async way)
  const onSelect = useCallback(
    (value: string) => {
      const filteredSuggestions = suggestions.filter((x) => x === value);
      setSuggestions(filteredSuggestions);
    },
    [suggestions]
  );

  const onFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const onBlurCallback = useCallback(
    (value: string) => {
      setIsFocused(false);
      if (onBlur) onBlur(value);
    },
    [onBlur]
  );

  return (
    <AutoComplete
      {...props}
      suggestions={suggestions}
      onChange={onChange}
      onBlur={onBlurCallback}
      onSelect={onSelect}
      onInputChange={setSearchTerm}
      onFocus={onFocus}
      autoComplete="off"
      label={label}
      value={value ?? ''}
      loading={loading}
      error={error}
      message={validationMessage}
      id={'autocomplete-' + dataCy}
    />
  );
};

export default DebouncedAutoComplete;
